﻿import { Checkbox, CheckboxProps, Flex } from 'antd'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import React, { useEffect, useMemo } from 'react'
import { SelectGroupOption, SelectOption } from '@/common/types/components.def.ts'
import { CheckboxOptionType } from 'antd/es/checkbox/Group'
import { cn } from '@/utils/style.ts'
import { stringArrayToSelectOptions } from '@/utils/misc.ts'
import {
  EmployeeDataSection,
  GroupedEmployeeDataSection
} from '@/common/types/employee-data-permission.def.ts'
import { useEmployeeDataAccessClaim } from '@/common/hooks/useEmployeeDataAccessClaim.ts'

type CustomCheckboxProps = {
  col?: number
  value?: string[]
  onChange?: (value: string[]) => void
  options: SelectOption[]
  disabled?: boolean
  showCheckAll?: boolean
}

export const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  value = [],
  onChange,
  options,
  disabled,
  showCheckAll = true,
  col,
}) => {
  const checkAll = options.length === value.length
  const indeterminate = value.length > 0 && value.length < options.length

  const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
    onChange?.(e.target.checked ? options.map(x => x.value) : [])
  }

  if (!showCheckAll) {
    return (
      <Checkbox.Group
        className={cn('grid grid-cols-4', {
          [`grid-cols-${col}`]: col && col > 0,
        })}
        options={options as CheckboxOptionType[]}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    )
  }

  return (
    <Flex vertical gap="small">
      <Checkbox
        className="font-semibold"
        indeterminate={indeterminate}
        onChange={onCheckAllChange}
        checked={checkAll}
        disabled={disabled}
      >
        All
      </Checkbox>
      <Checkbox.Group
        className={cn('grid grid-cols-4', {
          [`grid-cols-${col}`]: col && col > 0,
        })}
        options={options as CheckboxOptionType[]}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </Flex>
  )
}


type AntdFormCustomCheckboxProps = {
  col?: number
  options: SelectOption[]
  disabled?: boolean
} & AntdFormItemProps

type GroupedCheckboxProps = {
  /** Option groups, rendered in the given order. `key` is used as the React key and must be unique. */
  groups: SelectGroupOption[]
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
  col?: number
  /** Shown in place of the list when no group has any option. */
  emptyText?: string
}

/**
 * Checkbox list rendered as labelled groups with an "All" checkbox at both levels: one global, and
 * one per group. Shared by the employment-entity picker (grouped by country) and the employee data
 * section picker (grouped by section family).
 *
 * Purely presentational: it never filters or prunes `value`, so a caller whose option set can shrink
 * is responsible for dropping values that are no longer offered before handing them over.
 */
const GroupedCheckbox: React.FC<GroupedCheckboxProps> = ({
  groups,
  value = [],
  onChange,
  disabled,
  col,
  emptyText,
}) => {
  const allValues = groups.flatMap(group => group.options.map(x => x.value))

  if (allValues.length === 0) {
    return <span className="text-gray-400">{emptyText}</span>
  }

  // Interpolated rather than stacked on a base `grid-cols-*`, so the requested count always wins
  // regardless of stylesheet order. The `grid-cols-[1-8]` range is safelisted in tailwind.config.ts.
  const colClass = `grid-cols-${col && col > 0 ? col : 2}`

  const allChecked = allValues.every(x => value.includes(x))
  const allIndeterminate = value.length > 0 && !allChecked

  const onAllChange: CheckboxProps['onChange'] = (e) => {
    onChange?.(e.target.checked ? allValues : [])
  }

  const onGroupChange = (groupValues: string[], checked: boolean) => {
    onChange?.(checked
      ? Array.from(new Set([...value, ...groupValues]))
      : value.filter(x => !groupValues.includes(x)))
  }

  return (
    <Flex vertical gap="small">
      <Checkbox
        className="font-semibold"
        indeterminate={allIndeterminate}
        onChange={onAllChange}
        checked={allChecked}
        disabled={disabled}
      >
        All
      </Checkbox>

      {groups.map(group => {
        const groupValues = group.options.map(x => x.value)
        const groupChecked = groupValues.every(x => value.includes(x))
        const groupIndeterminate = groupValues.some(x => value.includes(x)) && !groupChecked

        return (
          <Flex key={group.key} vertical gap="small" className="pl-4">
            <Checkbox
              className="font-medium"
              indeterminate={groupIndeterminate}
              checked={groupChecked}
              disabled={disabled}
              onChange={e => onGroupChange(groupValues, e.target.checked)}
            >
              {group.label}
            </Checkbox>
            <Checkbox.Group
              className={cn('grid pl-6', colClass)}
              options={group.options as CheckboxOptionType[]}
              value={value.filter(x => groupValues.includes(x))}
              disabled={disabled}
              onChange={selected => onChange?.([
                ...value.filter(x => !groupValues.includes(x)),
                ...(selected as string[]),
              ])}
            />
          </Flex>
        )
      })}
    </Flex>
  )
}

export const AntdFormCheckbox: React.FC<AntdFormCustomCheckboxProps> = ({
  fieldProps,
  options,
  ...props
}) => {
  return (
    <AntdFormItem {...fieldProps}>
      <CustomCheckbox {...props} options={options} />
    </AntdFormItem>
  )
}

export const AntdFormManagedCountry: React.FC<Omit<AntdFormCustomCheckboxProps, 'options'>> = ({
  fieldProps,
  ...props
}) => {
  const { accessibleCountries } = useEmployeeDataAccessClaim()

  return (
    <AntdFormItem {...fieldProps}>
      <CustomCheckbox {...props} options={stringArrayToSelectOptions(accessibleCountries)} />
    </AntdFormItem>
  )
}

/**
 * Employment entity picker, offering only the entities the caller's access claim holds, grouped by
 * country with an "All" checkbox at both levels: one global, and one per country.
 *
 * Replaces AntdForm.ManagedCountry on the download/export forms: those requests now carry
 * `employmentEntities` instead of `countries`, because access is scoped by entity. Country is
 * derived from the entity, so a country "All" is just a shorthand for its entities.
 */
type ManagedEntitiesCheckboxProps = {
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
  col?: number
  /**
   * When non-empty, only entities whose claim grants EVERY one of these sections are offered.
   * Checked against the GENERAL section set, matching the server's export validation, so the
   * picker cannot offer an entity/section combination the request would then be rejected for.
   */
  targetSections?: string[]
}

const ManagedEntitiesCheckbox: React.FC<ManagedEntitiesCheckboxProps> = ({
  value = [],
  onChange,
  disabled,
  col,
  targetSections,
}) => {
  const { accessibleEntityOptions, entitiesWithAllSectionAccess } = useEmployeeDataAccessClaim()

  const sections = targetSections ?? []

  // Narrow the offered entities to those granting every target section. Memoized on the section
  // list contents rather than the array identity, so a caller passing a fresh literal each render
  // does not retrigger the pruning effect below.
  const sectionKey = sections.join('|')
  const entityOptions = useMemo(() => {
    if (sections.length === 0) return accessibleEntityOptions
    const allowed = entitiesWithAllSectionAccess(sections)
    return accessibleEntityOptions
    .map(group => ({ ...group, options: group.options.filter(x => allowed.includes(x.value)) }))
    .filter(group => group.options.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleEntityOptions, sectionKey])

  const allEntities = entityOptions.flatMap(group => group.options.map(x => x.value))

  // A selection made before the target sections narrowed could otherwise linger in form state
  // while being invisible in the UI, and would be rejected by the server. Drop any such value.
  const prunedValue = value.filter(x => allEntities.includes(x))
  useEffect(() => {
    if (prunedValue.length !== value.length) {
      onChange?.(prunedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prunedValue.join('|'), value.join('|')])

  return (
    <GroupedCheckbox
      groups={entityOptions}
      value={prunedValue}
      onChange={onChange}
      disabled={disabled}
      col={col}
      emptyText={sections.length > 0
        ? 'No employment entity grants access to the selected sections'
        : 'No employment entity access'}
    />
  )
}

export const AntdFormManagedEmploymentEntities: React.FC<
  Omit<AntdFormCustomCheckboxProps, 'options'> & { targetSections?: string[] }
> = ({
  fieldProps,
  ...props
}) => {
  return (
    <AntdFormItem {...fieldProps}>
      <ManagedEntitiesCheckbox {...props} />
    </AntdFormItem>
  )
}

const ignoreSectionForExport: string[] = [
  EmployeeDataSection.StaffCost,
  EmployeeDataSection.EmployeeDocument,
  EmployeeDataSection.StaffCost,
  EmployeeDataSection.TimeOff,
  EmployeeDataSection.OnboardingRecord,
  EmployeeDataSection.Checklist,
  EmployeeDataSection.Promotion,
  EmployeeDataSection.Probation,
  EmployeeDataSection.Overtime,
  EmployeeDataSection.Appraisal,
  EmployeeDataSection.RewardExercise,
]

/**
 * Display order of the named section families. Anything accessible that falls outside them lands in
 * "Others", so a section added to the backend still shows up without a change here.
 */
const sectionGroupDefinitions: { key: string, label: string, sections: string[] }[] = [
  { key: 'BasicInfo', label: 'Basic Info', sections: GroupedEmployeeDataSection.BasicInfo },
  { key: 'PmInfo', label: 'PM Info', sections: GroupedEmployeeDataSection.PmInfo },
  { key: 'Compensation', label: 'Compensation', sections: GroupedEmployeeDataSection.Compensation },
  {
    key: 'Others',
    label: 'Others',
    sections: Object.values(EmployeeDataSection).filter(x => !new Set(Object.values(GroupedEmployeeDataSection).flat()).has(x))
  },
]

export const AntdFormAccessibleEmployeeDataSection: React.FC<Omit<AntdFormCustomCheckboxProps, 'options'>> = ({
  fieldProps,
  ...props
}) => {
  const { accessibleSections } = useEmployeeDataAccessClaim()

  const sectionGroups: SelectGroupOption[] = useMemo(() => {
    const exportable = accessibleSections.filter(x => !ignoreSectionForExport.includes(x))
    const named = sectionGroupDefinitions.map(({ key, label, sections }) => ({
      key,
      label,
      // Keep the declared order within a named family: it reads in a deliberate sequence
      // (e.g. Work Info before Personal Info) rather than alphabetically.
      options: stringArrayToSelectOptions(sections.filter(x => exportable.includes(x))),
    }))

    return named.filter(group => group.options.length > 0)
  }, [accessibleSections])

  return (
    <AntdFormItem {...fieldProps}>
      <GroupedCheckbox
        {...props}
        groups={sectionGroups}
        col={props.col ?? 4}
        emptyText="No employee data section access"
      />
    </AntdFormItem>
  )
}